import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Network } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';
import { useNetworkTopology, useRouteRecords } from '../hooks/useNetworkTopology';
import { TopologyGraph } from '../components/TopologyGraph';
import { DeviceDetailPanel } from '../components/DeviceDetailPanel';
import type { NetworkDevice } from '../types';

interface SiteOption {
  id: string;
  name: string;
  mongoSiteId: string;
}

function useSites() {
  return useQuery({
    queryKey: ['sites-for-topology'],
    queryFn: () => api.get<SiteOption[]>('/v4/network-topology/sites'),
    staleTime: 5 * 60 * 1000,
  });
}

export function NetworkTopologyPage() {
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [selectedDevice, setSelectedDevice] = useState<NetworkDevice | null>(null);

  const { data: sites, isLoading: sitesLoading } = useSites();
  const { data: topology, isLoading: topologyLoading } = useNetworkTopology(
    selectedSiteId,
    autoRefresh,
  );
  const { data: routeData } = useRouteRecords(selectedSiteId, autoRefresh);

  const handleNodeClick = useCallback((device: NetworkDevice) => {
    setSelectedDevice(device);
  }, []);

  const handleCloseDetail = useCallback(() => {
    setSelectedDevice(null);
  }, []);

  const onlineCount = topology?.devices.filter(d => d.status === 'online').length ?? 0;
  const staleCount = topology?.devices.filter(d => d.status === 'stale').length ?? 0;
  const offlineCount = topology?.devices.filter(d => d.status === 'offline').length ?? 0;

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)]">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center gap-3">
          <Network className="h-5 w-5 text-muted-foreground" />
          <h1 className="text-lg font-semibold">Network Topology</h1>

          <Select
            value={selectedSiteId ?? ''}
            onValueChange={(val) => {
              setSelectedSiteId(val || null);
              setSelectedDevice(null);
            }}
          >
            <SelectTrigger className="w-[240px]">
              <SelectValue placeholder={sitesLoading ? 'Loading sites...' : 'Select a site'} />
            </SelectTrigger>
            <SelectContent>
              {(sites || []).map((site) => (
                <SelectItem key={site.id} value={site.mongoSiteId}>
                  {site.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-4">
          {topology && (
            <div className="flex items-center gap-3 text-sm">
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-green-500" />
                {onlineCount}
              </span>
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-amber-500" />
                {staleCount}
              </span>
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-red-500" />
                {offlineCount}
              </span>
            </div>
          )}

          <div className="flex items-center gap-2">
            <Switch
              id="auto-refresh"
              checked={autoRefresh}
              onCheckedChange={setAutoRefresh}
            />
            <Label htmlFor="auto-refresh" className="text-sm text-muted-foreground">
              Auto-refresh
            </Label>
          </div>
        </div>
      </div>

      {/* Graph area */}
      <div className="flex-1 relative">
        {!selectedSiteId && (
          <div className="absolute inset-0 flex items-center justify-center text-muted-foreground">
            Select a site to view its network topology
          </div>
        )}

        {selectedSiteId && topologyLoading && (
          <div className="absolute inset-0 flex items-center justify-center text-muted-foreground">
            Loading topology...
          </div>
        )}

        {selectedSiteId && topology && topology.devices.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center text-muted-foreground">
            No devices found at this site
          </div>
        )}

        {topology && topology.devices.length > 0 && (
          <TopologyGraph
            devices={topology.devices}
            routeRecords={routeData?.routes ?? []}
            onNodeClick={handleNodeClick}
            selectedDeviceId={selectedDevice?._id ?? null}
          />
        )}

        {/* Detail panel — overlaid at bottom of graph area */}
        {selectedDevice && (
          <div className="absolute bottom-0 left-0 right-0 z-10">
            <DeviceDetailPanel
              device={selectedDevice}
              routeRecords={routeData?.routes ?? []}
              onClose={handleCloseDetail}
            />
          </div>
        )}
      </div>
    </div>
  );
}
